import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, rupee } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Printer, ArrowLeft } from "lucide-react";

export default function InvoicePage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [o, setO] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!loading && !user) { nav("/admin/login"); return; }
    if (user) api.get(`/orders/${id}/invoice`).then((r) => setO(r.data)).catch(() => setErr(true));
  }, [user, loading, id]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-sm">Invoice not found.</div>;
  if (!o) return <div className="min-h-screen flex items-center justify-center text-sm">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FBF9F5] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-between mb-6 print:hidden">
          <button onClick={() => nav(-1)} className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#55524D]" data-testid="invoice-back"><ArrowLeft size={14} /> Back</button>
          <button onClick={() => window.print()} className="btn-lux flex items-center gap-2" data-testid="invoice-print"><Printer size={14} /> Print / Save PDF</button>
        </div>
        <div className="bg-white border border-[#E2DDD5] p-8 md:p-12" data-testid="invoice-document">
          <div className="flex justify-between items-start border-b-2 border-[#C5A059] pb-6">
            <div>
              <div className="font-serif text-3xl tracking-[0.35em]">LEE BAG</div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mt-2">Handcrafted Indian Leather Luxury</p>
            </div>
            <div className="text-right">
              <p className="font-serif text-xl">TAX INVOICE</p>
              <p className="font-mono text-xs mt-1" data-testid="invoice-id">{o.invoice_id}</p>
              <p className="text-xs text-[#8C867D]">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8 mt-8 text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#8C867D] mb-2">Billed To</p>
              <p className="font-medium">{o.customer_name || "Walk-in Customer"}</p>
              <p className="text-[#55524D]">{o.customer_email || ""}</p>
              {o.shipping_address && (
                <p className="text-[#55524D] text-xs mt-1">
                  {[o.shipping_address.line1, o.shipping_address.city, o.shipping_address.state, o.shipping_address.pincode].filter(Boolean).join(", ")}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#8C867D] mb-2">Order Reference</p>
              <p className="font-mono">{o.order_id}</p>
              <p className="text-xs text-[#55524D] mt-1 uppercase">Payment: {o.payment_method}</p>
              <p className="text-xs text-[#55524D] uppercase">Status: {o.status}</p>
              {o.emi_plan && <p className="text-xs text-[#5B1C22] mt-1">EMI {o.emi_plan.months}mo · Down payment {rupee(o.emi_plan.down_payment)} received</p>}
            </div>
          </div>

          <table className="w-full mt-8 text-sm">
            <thead>
              <tr className="border-b border-[#121212] text-[10px] uppercase tracking-[0.25em] text-[#8C867D]">
                <th className="text-left py-3">Item</th>
                <th className="text-center">Qty</th>
                <th className="text-right">Rate</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {o.items?.map((it, i) => (
                <tr key={i} className="border-b border-[#EFEAE1]">
                  <td className="py-3">{it.name}</td>
                  <td className="text-center">{it.quantity}</td>
                  <td className="text-right">{rupee(it.price)}</td>
                  <td className="text-right">{rupee(it.price * it.quantity)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-end mt-6">
            <div className="w-64 space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>{rupee(o.subtotal)}</span></div>
              {o.discount > 0 && <div className="flex justify-between text-[#5B1C22]"><span>Discount{o.coupon_code ? ` (${o.coupon_code})` : ""}</span><span>−{rupee(o.discount)}</span></div>}
              <div className="flex justify-between"><span>Tax</span><span>{rupee(o.tax)}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span>{o.shipping === 0 ? "Free" : rupee(o.shipping)}</span></div>
              <div className="flex justify-between font-medium text-base border-t-2 border-[#C5A059] pt-2 mt-2"><span>Total</span><span data-testid="invoice-total">{rupee(o.total)}</span></div>
            </div>
          </div>

          <div className="mt-12 pt-6 border-t border-[#E2DDD5] flex justify-between items-end">
            <div className="text-[10px] text-[#8C867D] uppercase tracking-[0.2em] leading-relaxed">
              LEE BAG · Handcrafted in India<br />
              UPI: {o.merchant_upi}<br />
              Each product carries a unique LEE BAG QR authenticity certificate.
            </div>
            <div className="text-right">
              <p className="font-serif italic text-lg text-[#C5A059]">LeeBag</p>
              <p className="text-[9px] uppercase tracking-[0.3em] text-[#8C867D]">Authenticated</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
